import * as THREE from "three";
import {
	clampTimeOfDay,
	type LegacyTimeOfDay,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";

/**
 * Where the chimney smoke leaves the flue, measured from the BUILT scene.
 *
 * Like the accent lights and the scale figures, the smoke is a built-scene
 * overlay: it reads the engines' own `detail/chimney/` meshes in the rig's
 * frame instead of re-deriving the flue position per engine, so it follows
 * heater moves, roof changes and building rotation for free. A design with
 * an electric heater builds no chimney, and so gets no smoke.
 */
export const CHIMNEY_MESH_PREFIX = "detail/chimney/";

/**
 * Meshes whose top sits within this band of the highest chimney point are
 * the cap/pipe end. Everything lower (the roof flashing, the storm collar,
 * the hearth plate inside) must not pull the vent's centre off the flue.
 */
const CAP_BAND_M = 0.25;

export interface ChimneyVent {
	/** Top centre of the flue in the rig's frame (metres). */
	position: [number, number, number];
	/** Half the flue's narrower horizontal extent, capped. */
	radius: number;
}

function isRenderable(object: THREE.Object3D, root: THREE.Object3D): boolean {
	let node: THREE.Object3D | null = object;
	while (node) {
		if (!node.visible) return false;
		if (node === root) return true;
		node = node.parent;
	}
	return true;
}

export function planChimneyVent(
	root: THREE.Object3D | null | undefined,
	frame: THREE.Object3D | null | undefined,
): ChimneyVent | null {
	if (!root || !frame) return null;
	root.updateWorldMatrix(true, true);
	frame.updateWorldMatrix(true, false);
	const toFrame = new THREE.Matrix4().copy(frame.matrixWorld).invert();
	const toFrameFromMesh = new THREE.Matrix4();

	const boxes: THREE.Box3[] = [];
	root.traverse((object) => {
		if (!object.name.startsWith(CHIMNEY_MESH_PREFIX)) return;
		const geometry = (object as THREE.Mesh).geometry as
			| THREE.BufferGeometry
			| undefined;
		if (!geometry || !isRenderable(object, root)) return;
		if (!geometry.boundingBox) geometry.computeBoundingBox();
		if (!geometry.boundingBox) return;
		toFrameFromMesh.multiplyMatrices(toFrame, object.matrixWorld);
		boxes.push(geometry.boundingBox.clone().applyMatrix4(toFrameFromMesh));
	});
	if (boxes.length === 0) return null;

	const topY = Math.max(...boxes.map((box) => box.max.y));
	const cap = new THREE.Box3();
	for (const box of boxes) {
		if (box.max.y >= topY - CAP_BAND_M) cap.union(box);
	}
	const center = cap.getCenter(new THREE.Vector3());
	const size = cap.getSize(new THREE.Vector3());
	return {
		position: [center.x, topY, center.z],
		radius: Math.min(Math.min(size.x, size.z) / 2, 0.15),
	};
}

export interface ChimneySmokeLook {
	/** Linear-space smoke colour, as the sky lights it. */
	color: THREE.Color;
	/** Peak opacity of one puff. */
	density: number;
}

const DAY_SMOKE = new THREE.Color(0xd8d6d0);
const DUSK_SMOKE = new THREE.Color(0x9a97a8);
const NIGHT_SMOKE = new THREE.Color(0x4c4b56);

/**
 * Smoke has no light of its own: in daylight it reads as a pale haze
 * against the treeline, at blue hour as a cool grey that the sky tints,
 * at night as a faint dark veil. It thins a little toward night because a
 * dark plume against a dark sky only reads at its densest.
 */
export function getChimneySmokeLook(
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number,
): ChimneySmokeLook {
	const t = clampTimeOfDay(normalizeTimeOfDay(timeOfDay));
	const toDusk = THREE.MathUtils.smoothstep(t, 0.45, 0.8);
	const toNight = THREE.MathUtils.smoothstep(t, 0.8, 1);
	const color = DAY_SMOKE.clone()
		.lerp(DUSK_SMOKE, toDusk)
		.lerp(NIGHT_SMOKE, toNight);
	// Per puff; overlapping puffs build the plume up to roughly 3x this.
	return { color, density: THREE.MathUtils.lerp(0.24, 0.18, toNight) };
}
